import React from "react";
import { connect } from "react-redux";
import type { Dispatch } from "redux";

import ErrorHandler from "../ErrorHandler";
import OrderInsightsDiff from "../OrderInsightsDiff";
import Tabs from "../../../react/Tabs";

import StockRequirementsTabContent, { StockRequirementsTabHeader } from "./StockRequirements";
import InsightsTabContent, { InsightsTabHeader, type InsightStats } from "./Insights";
import OrdersTabContent, { OrdersTabHeader } from "./Orders";
import IgnoredOrdersTabContent, { IgnoredOrdersTabHeader } from "./IgnoredOrders";
import { PlanningDetailsViews } from "./common";
import { LINE_GROUP_TAGS_ACCESS } from "../../../../lib/ManufacturingTags.generated";
import { getLineGroupTags, shouldAddExtraColumns } from "../../../../lib/BusinessLogic";
import { ToolSetupLabelHistory } from "../lineOrderList/ToolSetupLabel";
import { SET_UNSCHEDULED_SORT_SETTINGS } from "../reducers/linesOrders";
import PlanningTableColumns from "./PlanningTableColumns";

import type { TabConfig } from "../../../react/Tabs";
import type { UpdatePropertiesActionType, ResetProperties } from "../reducers/properties";
import type { SetRescheduleOrderAction } from "../reducers/common";
import type { SetTranslationsMaterials } from "../../../redux/reducers/translationsMaterialsReducer";
import type { ResetReportAction } from "../reducers/report";
import type {
    LoadingAction,
    UpdateLinesOrdersAction,
    ResetLinesOrdersAction,
    SetUnscheduledOrdersAction,
    OperationSortBy,
    OperationSortDirection,
    SetUnscheduledSortSettings,
} from "../reducers/linesOrders";
import type { ResetValidLinesAction, SetErrorAction } from "../reducers/planningTable";
import type { ReduxState } from "../reducers/reducers";
import type { ResetGanttAction } from "../reducers/common";
import type { PlanningDetailsViewsType } from "./common";
import type { PlanningDetailsPosition } from "../../../ViewsCommon";

type ActionTypes =
    | LoadingAction
    | UpdateLinesOrdersAction
    | ResetLinesOrdersAction
    | ResetReportAction
    | SetUnscheduledOrdersAction
    | ResetValidLinesAction
    | ResetGanttAction
    | UpdatePropertiesActionType
    | ResetProperties
    | SetTranslationsMaterials
    | SetRescheduleOrderAction
    | SetErrorAction
    | SetUnscheduledSortSettings;

type OwnProps = {
    plant_uuid: string;
    line_group_uuid: string;
    show_unscheduled_orders: boolean;
    show_planning_details_filter: boolean;
    position: PlanningDetailsPosition;
    size_ratio: number;
};

type Props = OwnProps & ReduxProps;

type State = {
    insight_stats: InsightStats;
    show_modal_line_title: string | null;
};

class PlanningDetails extends React.Component<Props, State> {
    state: State = {
        show_modal_line_title: null,
        insight_stats: {
            solved: 0,
            unsolved: 0,
        },
    };

    constructor(props: Props) {
        super(props);

        PlanningTableColumns.createInstance();
        if (shouldAddExtraColumns()) {
            this.addExtraColumns();
        }
    }

    // Hack
    addExtraColumns = () => {
        PlanningTableColumns.getInstance().addColumnIds(["material_deadline", "material_tool_setup"], {
            after_column_id: "material_title",
            table_size: "big",
        });
    };

    getHasUnscheduledOrders = (): boolean => {
        const orders = this.props.unscheduled_orders.flatMap(lo => lo.orders);
        return orders.length > 0;
    };

    onClickInfo = (show_modal_line_title: string) => (e: React.MouseEvent<HTMLElement>) => {
        e.preventDefault();
        e.stopPropagation();
        this.setState({ show_modal_line_title });
    };

    updateInsightsStats = (solved: number, unsolved: number) => {
        this.setState({ insight_stats: { solved, unsolved } });
    };

    onCloseModal = () => {
        this.setState({
            show_modal_line_title: null,
        });
    };

    handleSortChange = (unscheduled_sort_by: OperationSortBy, unscheduled_sort_direction: OperationSortDirection) => {
        this.props.reduxDispatch({
            type: SET_UNSCHEDULED_SORT_SETTINGS,
            data: {
                unscheduled_sort_by,
                unscheduled_sort_direction,
            },
        });
    };

    getTabsData = (): TabConfig<PlanningDetailsViewsType>[] => {
        const data: TabConfig<PlanningDetailsViewsType>[] = [
            {
                title: <OrdersTabHeader />,
                id: PlanningDetailsViews.orders,
                content: (
                    <OrdersTabContent
                        show_planning_details_filter={this.props.show_planning_details_filter}
                        columns={PlanningTableColumns.getInstance().getColumns(
                            this.props.position,
                            this.props.size_ratio
                        )}
                        unscheduled_columns={PlanningTableColumns.getInstance().getUnscheduledColumns()}
                        show_unscheduled_orders={this.props.show_unscheduled_orders}
                        line_group_uuid={this.props.line_group_uuid}
                        plant_uuid={this.props.plant_uuid}
                        lines_orders_loading={this.props.lines_orders_loading}
                        lines_orders={this.props.lines_orders}
                        unscheduled_sort_by={this.props.unscheduled_sort_by}
                        unscheduled_sort_direction={this.props.unscheduled_sort_direction}
                        onClickInfo={this.onClickInfo}
                        onSortChange={this.handleSortChange}
                        highlighted_move_order_uuid={this.props.highlighted_move_order_uuid}
                        campaign_reference_key={this.props.campaign_reference_key}
                    />
                ),
            },
            {
                title: <IgnoredOrdersTabHeader />,
                id: PlanningDetailsViews.ignored_orders,
                content: <IgnoredOrdersTabContent lines_orders_loading={this.props.lines_orders_loading} />,
            },
        ];

        const line_group_tags =
            this.props.properties.line_group_uuid !== null
                ? getLineGroupTags(this.props.properties.line_group_uuid)
                : {};
        const show_issues_tab = LINE_GROUP_TAGS_ACCESS.planning_table_show_issues_tab(line_group_tags);
        if (show_issues_tab) {
            data.push({
                title: <InsightsTabHeader insights_stats={this.state.insight_stats} />,
                id: PlanningDetailsViews.insights,
                content: (
                    <InsightsTabContent report={this.props.report} updateInsightsStats={this.updateInsightsStats} />
                ),
            });
        }

        if (this.props.show_stock_requirements) {
            data.splice(2, 0, {
                title: <StockRequirementsTabHeader />,
                id: PlanningDetailsViews.stock_requirements,
                content: (
                    <StockRequirementsTabContent
                        report={this.props.report}
                        show_planning_details_filter={this.props.show_planning_details_filter}
                        line_group_uuid={this.props.line_group_uuid}
                        lines_orders={this.props.lines_orders}
                    />
                ),
            });
        }
        return data;
    };

    render() {
        const { position, show_planning_details_filter, size_ratio } = this.props;

        const size_percentage = `${size_ratio * 100}%`;

        const styles_dimension =
            position === "left" ? { width: size_percentage } : { bottom: 0, height: size_percentage };

        const styles = {
            display: show_planning_details_filter ? "flex" : "none",
            ...styles_dimension,
        };

        return (
            <div
                className={`planning-table-sidebar2 flex-column overflow-auto planning-table--${position}`}
                style={styles}
            >
                <ErrorHandler />
                <ToolSetupLabelHistory />
                <Tabs data={this.getTabsData()} active_tab={this.props.enforce_orders_view_change} />
                <OrderInsightsDiff
                    show_order_line_title={this.state.show_modal_line_title}
                    onCloseModal={this.onCloseModal}
                />
            </div>
        );
    }
}

const mapState = (state: ReduxState) => {
    const report = state.gantt_chart_report;
    const planning_table = state.gantt_chart_planning_table;
    const lines_orders = state.gantt_chart_lines_orders.lines_orders;
    const loading = state.gantt_chart_lines_orders.loading;
    const properties = state.gantt_chart_properties;

    return {
        report,
        lines_orders_loading: loading,
        lines_orders,
        unscheduled_orders: state.gantt_chart_lines_orders.unscheduled_orders,
        properties,
        enforce_orders_view_change: planning_table.selected_planning_details_view,
        show_stock_requirements: planning_table.show_stock_requirements,
        unscheduled_sort_by: state.gantt_chart_lines_orders.unscheduled_sort_by,
        unscheduled_sort_direction: state.gantt_chart_lines_orders.unscheduled_sort_direction,
        campaign_reference_key: planning_table.campaign_reference_key,
        highlighted_move_order_uuid: planning_table.highlighted_move_order_uuid,
    };
};

const mapDispatch = (dispatch: Dispatch<ActionTypes>) => ({
    reduxDispatch: dispatch,
});

type ReduxProps = ReturnType<typeof mapState> & ReturnType<typeof mapDispatch>;

export default connect<ReturnType<typeof mapState>, ReturnType<typeof mapDispatch>, OwnProps, ReduxState>(
    mapState,
    mapDispatch
)(PlanningDetails);
